import React, {useEffect, useState} from 'react';
import classNames from 'classnames';
import {useDispatch} from 'react-redux';
import {InfoCard} from "@eika/card";
import {DefaultConfig} from '../../default-config';
import Aktivering from '../../components/Aktivering/Aktivering';
import LoggInnPaAvtale from '../../components/LoggInnPaAvtale/LoggInnPaAvtale';
import {generateId, scrollBodyTop} from '../../common/util/Functions';
import {useActivation, useTranslation} from '../../common/util/Hook';
import {StringObject} from '../../types';
import {ActivateOrderParam, ActivateOrderResult, StartActivationParam, StartActivationResult,} from '../../generated-types';
import Constant from '../../static/data/Constant';

import {getActivation, postActivateOrder, setActivateOrder, setActivation, setSendPassword} from '../../actions/activation';

import './Avtale.scss';
import '@eika/layout-style'
import Spinner from "../../common/Spinner/Spinner";

interface Props {
    config: typeof DefaultConfig;
}

interface StepItem {
    id: string;
    title?: string;
    description?: string | string[];
    error?: boolean;
    element?: React.ReactElement;
}

interface State {
    id: string;
    error: string;
    message: string;
    loading: boolean;
    dispatch: (s: any) => void;
    translated: StringObject;
    stepView: {
        selected: number;
        stepList: StepItem[];
    };
}

const login = (state: State, setState: (s: State) => void, data: StringObject): void => {
    state.dispatch(setActivation(undefined));
    setState({...state, loading: true, error: ''});

    const param: StartActivationParam = {
        customerId: data.customerId || '',
        otp: data.otp || '',
    };
    state.dispatch(getActivation(param));
};

const activate = (state: State, setState: (s: State) => void, data: StringObject, activation?: StartActivationResult): void => {
    state.dispatch(setActivateOrder(undefined));
    setState({...state, loading: true, error: ''});

    const param: ActivateOrderParam = {
        password: data.password || '',
        reference: activation?.reference || '',
    };

    state.dispatch(postActivateOrder(param));
};

const startOver = (state: State, setState: (s: State) => void): void => {
    setState({...state, error: '', stepView: {...state.stepView, selected: 0}})

    state.dispatch(setActivation(undefined));
    state.dispatch(setActivateOrder(undefined));
    state.dispatch(setSendPassword(undefined));
}

const updateState = (state: State, setState: (s: State) => void, activation?: StartActivationResult, activated?: ActivateOrderResult): void => {
    const base = {loading: false, error: '', message: ''};
    const defaultText = state.translated.opsNoeGikkGalt;

    if (activation === null && !state.error) {
        setState({...state, ...base, error: defaultText});
    } else if (activation?.success === true) {
        if (activated === null && !state.error) {
            setState({...state, ...base, error: defaultText});
        } else if (activated?.success === true && state.stepView.selected !== 2) {
            scrollBodyTop(undefined, undefined, state.id, -40);
            setState({...state, ...base, stepView: {...state.stepView, selected: 2}});
        } else if (activated?.success === false) {
            const text = state.translated[activated?.result] || activated?.result || defaultText;
            if (!state.error) {
                setState({...state, ...base, error: text});
            }
        } else if (state.stepView.selected === 0) {
            scrollBodyTop(undefined, undefined, state.id, -40);
            setState({...state, ...base, stepView: {...state.stepView, selected: 1}});
        }
    } else if (activation?.success === false) {
        const text = state.translated[activation?.result] || activation.result || defaultText;
        if (activation?.result === Constant.avtaleOTPnoOrder) {
            if (!state.message) {
                setState({...state, ...base, message: text});
            }
        } else if (!state.error) {
            setState({...state, ...base, error: text});
        }
    }
};

export default (props: Props): React.ReactElement => {
    const dispatch = useDispatch();
    const {activation, activated} = useActivation();
    const translated = useTranslation();
    const [state, setState] = useState<State>({
        id: generateId('avtale'),
        error: '',
        message: '',
        loading: false,
        dispatch,
        translated,
        stepView: {
            selected: 0,
            stepList: [{
                id: 'bank-id-avtale-start',
            }, {
                id: 'bank-id-avtale-aktivering',
            }, {
                id: 'bank-id-avtale-slutt',
                title: translated.bankIdAvtaleSluttTittel,
                description: [
                    translated.bankIdAvtaleSluttBeskrivelse1,
                    translated.bankIdAvtaleSluttBeskrivelse2,
                ]
            }]
        },
    });

    useEffect(() => {
        updateState(state, setState, activation, activated);
    }, [state, setState, activation, activated]);

    return (
        <InfoCard id={state.id} className={classNames('avtale-wrapper', `-step-${state.stepView.selected}`, {
            '-no-bank-id-ordered': !!state.message,
        })}>
            <h1 className="avtale-title paragraph">{translated.bankIdAvtale}</h1>
            {state.loading && <Spinner namespace="avtale-loader"/>}
            {!!state.message && <p aria-live="polite" className="avtale-message">{state.message}</p>}

            <div className="step-panel-wrapper">
                {state.stepView.stepList.map((data: StepItem, i: number) => {
                    return i === state.stepView.selected ?
                        <div id={`${data.id}-panel`} key={`${data.id}-panel`} className="step-panel">
                            {!!data.title && <h2 className="paragraph-small">{data.title}</h2>}

                            {(data.description instanceof Array ? data.description : [data.description]).map((text?: string) => {
                                return text ? <p key={`description-${i}-${text || 'none'}`} className="paragraph-small">{text}</p> : null
                            })}

                            {data.id === 'bank-id-avtale-start' &&
                                <LoggInnPaAvtale {...props} submit={(data: StringObject) => {
                                    login(state, setState, data);
                                }} failed={state.error}/>
                            }

                            {data.id === 'bank-id-avtale-aktivering' &&
                                <Aktivering {...props} activation={activation} useH2 submit={(data: StringObject) => {
                                    activate(state, setState, data, activation);
                                }} failed={state.error} startOver={() => startOver(state, setState)}/>
                            }
                            {data.element}
                        </div>
                        : null
                })}
            </div>
        </InfoCard>
    );
};
